/* eslint-disable import/no-anonymous-default-export */
import axios from "axios";
import toastr from "toastr";
import Cookies from "universal-cookie";
import store from "../store";
import { logout, login } from "../utils/redux/auth/authActions";
import refreshAuthToken from "./helpers/refreshAuthToken";
import { getEnv } from "./env";

let cancelSource = axios.CancelToken.source();

const instance = axios.create({
  baseURL: getEnv("REACT_APP_API_URL", { required: true }),
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

export const handleCancelRequest = () => {
  cancelSource.cancel("Operation canceled by the user.");
};

export default {
  setupInterceptors: (navigate, setRequestLoading) => {
    instance.interceptors.request.use(
      function (config) {
        cancelSource = axios.CancelToken.source();
        config.cancelToken = cancelSource.token;
        const user = new Cookies().get("user");

        if (
          user?.isLoggedIn &&
          user?.access_token &&
          !config.url.includes("refresh_token")
        ) {
          config.headers["Authorization"] = `Bearer ${user.access_token}`;
        }
        return config;
      },
      function (error) {
        return Promise.reject(error);
      }
    );

    instance.interceptors.response.use(
      function (response) {
        setRequestLoading(false);
        return response;
      },
      function (error) {
        setRequestLoading(false);
        if (axios.isCancel(error)) {
          return Promise.reject(error.message || "Request canceled");
        }
        if (
          error.response &&
          error.response.status >= 400 &&
          error.response.status <= 500
        ) {
          if (error.response.status === 401) {
            const user = new Cookies().get("user");
            handleCancelRequest();

            if (
              user?.isLoggedIn &&
              user?.refresh_token &&
              !error.config.url.includes("refresh_token")
            ) {
              refreshAuthToken(user.refresh_token)
                .then((data) => {
                  const newUser = {
                    ...user,
                    access_token: data.access_token,
                  };

                  store.dispatch(login(newUser));
                })
                .then(() => {
                  window.location.reload(false);
                });
              return;
            } else {
              store.dispatch(logout());
            }
          }

          if (
            error.response.status === 400 &&
            error.config.url.includes("refresh_token")
          ) {
            store.dispatch(logout());
          }

          if (
            error.response.status === 403 &&
            !getEnv("REACT_APP_DEV_MODE")
          ) {
            const user = new Cookies().get("user");
            if (!user?.role?.name) {
              store.dispatch(logout());
            }
          }

          const errorMessage =
            error?.response?.data?.error_message ||
            error.response?.data?.message ||
            `${error.response.status} ${error.response.statusText}` ||
            null;

          if (errorMessage) {
            toastr.clear();
            toastr.error(errorMessage);
          }

          return Promise.reject(new Error(errorMessage));
        }

        return Promise.reject(error);
      }
    );
  },
  getData: (url) =>
    instance({
      method: "GET",
      url,
    }),
  postData: (url, data = {}) =>
    instance({
      method: "POST",
      url,
      data,
    }),
  postFile: (url, data, config = {}) =>
    instance({
      method: "POST",
      url,
      data,
      ...config,
      headers: {
        ...config.headers,
        "Content-Type": undefined,
      },
    }),
  putData: (url, data = {}) =>
    instance({
      method: "PUT",
      url,
      data,
    }),
  putFile: (url, data, config = {}) =>
    instance({
      method: "PUT",
      url,
      data,
      ...config,
      headers: {
        ...config.headers,
        "Content-Type": undefined,
      },
    }),
  deleteData: (url) =>
    instance({
      method: "DELETE",
      url,
    }),
  login: (data) =>
    instance({
      method: "POST",
      url: "/auth/login",
      data,
    }),
  getUserData: (id = null, token = null) =>
    token
      ? instance({
          method: "GET",
          url: "/user" + (id ? `/${id}` : ""),
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      : instance({
          method: "GET",
          url: "/user" + (id ? `/${id}` : ""),
        }),
  resetPassword: (token = null, data) =>
    instance({
      method: "POST",
      url: "/auth/reset_password",
      data,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
  updateUserData: (data, id = null, company_id = null) => {
    let url = "user" + (id ? `/${id}` : "");

    url += company_id ? `?company_id=${company_id}` : "";

    return instance({
      method: "PUT",
      url,
      data,
    });
  },
  createUser: (project_id, company_id = null, data = null) => {
    let url = "users?";

    url += `project_id=${project_id}`;
    url += company_id ? `&company_id=${company_id}` : "";

    return instance({
      method: "POST",
      url,
      data,
    });
  },
  getCompanyData: (id) =>
    instance({
      method: "GET",
      url: `company/${id}`,
    }),
  updateCompanyData: (data, id = null) =>
    instance({
      method: "PUT",
      url: `company/${id}`,
      data,
    }),
  getCompanies: (project = null, page = null, size = null, search = null) => {
    let url = "companies?";

    url += project ? `project_id=${project}&` : "";
    url += page ? `page=${page}&` : "";
    url += size ? `size=${size}&` : "";
    url += search ? `search=${search}&` : "";

    return instance({
      method: "GET",
      url,
    });
  },
  getProjects: (args = { query: null, type: null, deleted: false }) => {
    const { query, type, deleted } = args;
    let url = "projects?";

    url += query ? `search=${query}&` : "";
    url += type ? `place=${type}&` : "";
    url += deleted ? `deleted=true&` : "";

    return instance({
      method: "GET",
      url,
    });
  },
  getCompanyUsers: (
    project_id = null,
    company_id = null,
    page = null,
    size = null,
    search = null,
    sortBy = "user_name",
    orderBy = "asc"
  ) => {
    let url = "users?";

    url += project_id ? `project_id=${project_id}&` : "";
    url += company_id ? `company_id=${company_id}&` : "";
    url += page ? `page=${page}&` : "";
    url += size ? `size=${size}&` : "";
    url += search ? `search=${search}&` : "";
    url += `sort_by=${sortBy}&order_by=${orderBy}`;

    return instance({
      method: "GET",
      url,
    });
  },
  getCompanyGroups: (
    company_id = null,
    page = null,
    size = null,
    project_id = null
  ) => {
    let url = "groups?";

    url += company_id ? `company_id=${company_id}&` : "";
    url += page ? `page=${page}&` : "";
    url += size ? `size=${size}&` : "";
    url += project_id ? `project_id=${project_id}&` : "";

    return instance({
      method: "GET",
      url,
    });
  },
  getRoles: (project_id = null, company_id = null) => {
    let url = "roles?";

    url += project_id ? `project_id=${project_id}&` : "";
    url += company_id ? `company_id=${company_id}&` : "";

    return instance({
      method: "GET",
      url,
    });
  },
  getFormTemplates: (
    project_id = null,
    page = null,
    size = null,
    search = null,
    create = false,
    data
  ) => {
    let url = "form_templates/filters?";

    url += project_id ? `project_id=${project_id}&` : "";
    url += page ? `page=${page}&` : "";
    url += size ? `size=${size}&` : "";
    url += search ? `search=${search}&` : "";
    url += create ? `create=${create}` : "";

    return instance({
      method: "POST",
      url,
      data,
    });
  },
  /**
   * POST that returns a streaming axios response (response.data is a ReadableStream).
   * Pass a plain object to send JSON (same as postData).
   * Auth, baseURL and error interceptors are applied via the shared instance.
   */
  postDataStream: (url, data = {}, config = {}) =>
    instance({
      method: "POST",
      url,
      data,
      responseType: "text",
      ...config,
      headers: {
        Accept: "application/x-ndjson, application/json",
        ...(config.headers || {}),
      },
    }),
};
